import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { BaseRange, Editor, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { AgentPanelLogo } from '../../../molecules/AgentPanel/AgentPanelLogo';
import { DropdownItem } from '../../../molecules/Dropdown/DropdownItem';
import { DropdownPopup } from '../../../molecules/Dropdown/DropdownPopup';
import { CustomText, MentionOption } from '../types';

const Portal = ({ children }: { children: ReactNode }) =>
  typeof document === 'object' ? createPortal(children, document.body) : null;

type State = { show: boolean; search: string; index: number; target?: BaseRange };
const defaultState: State = { show: false, search: '', index: 0, target: undefined };

/**
 * Optional @-mention typeahead for RichTextEditor. Detects `@word` at the caret,
 * shows a dropdown of the provided options, and inserts an inline `mention` node
 * on select. Mirrors the desk internal-note mention behaviour (useInitMention)
 * and reuses the same UI primitives (DropdownPopup/DropdownItem/AgentPanelLogo),
 * but is generic — the consumer passes the options (id + label); this hook has
 * no knowledge of agents/users or the data layer (so it can live in `ui`).
 *
 * `onValueChange` is called from Slate's onChange; `onKeyDown` returns true when
 * it consumed the key (so the editor can skip its own handling); `dropdown` is
 * the portal to render.
 */
export function useMentions(editor: Editor, options: MentionOption[]) {
  const [state, setState] = useState<State>(defaultState);
  const enabled = options.length > 0;
  const isShow = state.show;

  const onValueChange = useCallback(() => {
    if (!enabled) return;
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);

      const beforeAnchor = Editor.before(editor, selection.anchor);
      const beforeAnchorRange =
        beforeAnchor && Editor.range(editor, beforeAnchor, selection.anchor);
      const isAt = beforeAnchorRange && Editor.string(editor, beforeAnchorRange) === '@';

      const wordBefore = Editor.before(editor, start, { unit: 'word' });
      const before = wordBefore && Editor.before(editor, wordBefore);
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);

      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = afterText.match(/^(\s|$)/);

      // The `@` must start a word (preceded by whitespace or the block start),
      // otherwise it's part of an email/handle like "sem@host.com" — not a
      // mention. Check the character immediately left of the `@`.
      const atStart = isAt ? beforeAnchor : before;
      const prevPoint = atStart && Editor.before(editor, atStart, { unit: 'character' });
      const prevRange = prevPoint && atStart && Editor.range(editor, prevPoint, atStart);
      const prevChar = prevRange ? Editor.string(editor, prevRange) : '';
      const isMentionStart = !prevChar || /\s/.test(prevChar);

      if (afterMatch && isMentionStart && (isAt || beforeMatch)) {
        setState({
          show: true,
          search: beforeMatch?.[1] || '',
          index: 0,
          target: (isAt ? beforeAnchorRange : beforeRange) || undefined,
        });
        return;
      }
    }
    if (isShow) setState(defaultState);
  }, [editor, enabled, isShow]);

  const { show, search, index, target } = state;

  const filtered = useMemo(() => {
    if (!search) return options;
    const s = search.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(s));
  }, [options, search]);

  const length = filtered.length;

  const insert = useCallback(
    (customIndex?: number) => {
      if (!target) return;
      const option = filtered[typeof customIndex !== 'undefined' ? customIndex : index];
      if (!option) return;
      Transforms.select(editor, target);
      Transforms.insertNodes(editor, {
        type: 'mention',
        mention: option,
        children: [{ text: '' }] as CustomText[],
      });
      Transforms.move(editor);
      setState(defaultState);
      // Keep the caret in the editor right after the mention.
      ReactEditor.focus(editor);
    },
    [editor, filtered, index, target],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!show || !length) return false;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setState((st) => ({ ...st, index: st.index >= length - 1 ? 0 : st.index + 1 }));
          return true;
        case 'ArrowUp':
          event.preventDefault();
          setState((st) => ({ ...st, index: st.index <= 0 ? length - 1 : st.index - 1 }));
          return true;
        case 'Tab':
        case 'Enter':
          event.preventDefault();
          insert();
          return true;
        case 'Escape':
          event.preventDefault();
          setState(defaultState);
          return true;
        default:
          return false;
      }
    },
    [show, length, insert],
  );

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!show || !target || !ref.current) return;
    try {
      const menu = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      const menuHeight = menu.offsetHeight;
      const menuWidth = menu.offsetWidth;
      const gap = 8;

      // Flip above the caret when there isn't enough room below.
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + gap && rect.top > spaceBelow;
      const top = openUp
        ? Math.max(gap, rect.top - menuHeight - gap)
        : Math.min(rect.bottom + gap, window.innerHeight - menuHeight - gap);

      // Keep within the horizontal viewport too.
      const left = Math.min(rect.left, window.innerWidth - menuWidth - gap);

      menu.style.top = `${Math.max(gap, top)}px`;
      menu.style.left = `${Math.max(gap, left)}px`;
    } catch {
      // The target range can be momentarily stale between renders — ignore.
    }
  }, [target, show, editor, index, length]);

  const dropdown = useMemo(() => {
    if (!show || !length) return null;
    return (
      <Portal>
        {/* fixed → matches getBoundingClientRect viewport coords; z above the
            detached composer modal (z-1000). Top/left set in the effect with
            viewport-edge flipping/clamping. onMouseDown preventDefault on the
            whole menu so clicking any part of it never blurs the editor. */}
        <div ref={ref} className="fixed z-[1100]" onMouseDown={(event) => event.preventDefault()}>
          <DropdownPopup
            className="static m-0 max-h-[40vh] w-[280px] overflow-auto"
            position="top"
            open
          >
            {filtered.map((option, i) => {
              const abbr = option.label
                .split(' ')
                .map((word) => word[0])
                .join('')
                .slice(0, 2);
              return (
                // onMouseDown (not onClick) + preventDefault: insert before the
                // editor can blur, so focus stays in the editor.
                <div
                  key={option.id}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insert(i);
                  }}
                >
                  <DropdownItem className="px-3" selected={i === index}>
                    <div className="flex w-full items-center gap-2">
                      <AgentPanelLogo name={option.label} size="small" color="random">
                        {abbr}
                      </AgentPanelLogo>
                      <div className="overflow-hidden overflow-ellipsis text-sm">
                        {option.label}
                      </div>
                    </div>
                  </DropdownItem>
                </div>
              );
            })}
          </DropdownPopup>
        </div>
      </Portal>
    );
  }, [show, length, filtered, index, insert]);

  return { onValueChange, onKeyDown, dropdown };
}
